import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";

// ModalShell — the one dialog scaffold every modal in the app shares.
//
// Before this existed, each modal hand-rolled the same four concerns with two
// drifting conventions: the card modals (WontDoReasonModal, InsightCitations,
// InsightHistory, FullListModal) used createPortal + a global Escape listener
// + an onMouseDown backdrop check; the connector pickers (GA4 / Meta) used no
// portal, no Escape, and an onClick backdrop dismiss — so they inherited the
// `transform`-clipping bug the card modals' portal comments warn about. This
// shell is the card-modal convention, made reusable:
//
//   - createPortal to <body> so an ancestor's `transform` (kanban drag
//     wrappers etc.) can't turn our `position: fixed` backdrop into
//     `position: absolute` and clip it behind the next card;
//   - role="dialog" + aria-modal + aria-labelledby (the caller owns the
//     heading element and passes its id via `labelledById`);
//   - Escape-to-close, bound on `document` so the keydown fires regardless of
//     which child holds focus. Children that need to intercept Escape first
//     (FullListModal's "clear the search, then close" two-stage) call
//     `event.stopPropagation()` in their own handler — that stops native
//     bubbling before it reaches this listener, exactly as before;
//   - mousedown-backdrop dismiss (mousedown, not click, so a text selection
//     that starts inside the panel and ends on the backdrop doesn't dismiss);
//   - focus handed back to whatever was focused when the shell mounted (the
//     trigger button) on unmount, so screen-reader users aren't dropped at the
//     document root.
//
// Visual parity is preserved by passing the existing classNames through:
// `backdropClassName` / `panelClassName` keep each modal's exact CSS hooks.
// The optional `accent` sets the `--card-accent` custom property the accented
// panels read.

export type ModalShellProps = {
  // The id of the heading element the caller renders inside `children`; wired
  // to aria-labelledby so the dialog announces its own title.
  labelledById: string;
  // Existing CSS hooks, passed through verbatim for pixel parity.
  backdropClassName: string;
  panelClassName: string;
  // Sets `--card-accent` on the panel when provided (accented modals).
  accent?: string;
  onClose: () => void;
  children: ReactNode;
};

export function ModalShell({
  labelledById,
  backdropClassName,
  panelClassName,
  accent,
  onClose,
  children,
}: ModalShellProps) {
  // Keep the latest onClose without re-subscribing the document listener every
  // render — callers frequently pass a fresh arrow each render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Escape-to-close, bound globally while mounted.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Hand focus back to the opener (trigger button) when the modal unmounts.
  useEffect(() => {
    const opener = document.activeElement;
    return () => {
      if (opener instanceof HTMLElement && document.contains(opener)) {
        opener.focus();
      }
    };
  }, []);

  const panelStyle: CSSProperties | undefined =
    accent !== undefined
      ? ({ ["--card-accent" as string]: accent } as CSSProperties)
      : undefined;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledById}
      className={backdropClassName}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={panelClassName} style={panelStyle}>
        {children}
      </div>
    </div>,
    document.body,
  );
}
